// Master Apps · dados de acesso (unidades, setores e colaboradores do TOTVS).
//
// Alimenta a etapa "Acesso e confidencialidade" do cadastro de aplicação:
// quem pode visualizar uma aplicação privada (unidades → setores → pessoas).
//
// TRANSPORTE: RPC tipado (`apiClient.api.totvs.*`) do `lib/api-client.ts`, que
// já anexa o Bearer do Clerk e usa `credentials: 'include'`. As três rotas
// entraram no `AppType`, então o RPC compila e os tipos declarados aqui passam
// a ser conferidos contra a API (se o servidor mudar a forma, o build reclama).
// Se o `AppType` ficar temporariamente sem essas rotas, o caminho alternativo é
// um `fetch` relativo para `/api/totvs/...` repetindo o mesmo cabeçalho de
// autenticação — nada mais neste arquivo muda.

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { readApiError } from '@/hooks/use-applications';
import { apiClient } from '@/lib/api-client';

// ─── Contrato dos endpoints ───────────────────
/** `GET /api/totvs/units` · 9 unidades reais. */
export interface AccessUnit {
  codFilial: string;
  nome: string;
  /** Razões sociais quando o mesmo código de filial tem mais de uma. */
  nomes: string[];
  total: number;
}

export interface AccessSectorUnit {
  codFilial: string;
  nome: string;
}

/** `GET /api/totvs/sectors?units=1,11` · já DISTINCT por nome de setor. */
export interface AccessSector {
  setorNome: string;
  /** Unidades que possuem este setor · exibidas ao lado do nome. */
  unidades: AccessSectorUnit[];
  total: number;
}

/** `GET /api/totvs/collaborators?units=&sectors=&search=&limit=`. */
export interface AccessCollaborator {
  matricula: string;
  nome: string;
  funcao: string | null;
  setorNome: string | null;
  codFilial: string | null;
  filial: string | null;
}

// ─── Transporte ───────────────────────────────
/** Cadastro de RH muda pouco · 5 min de cache evita ida e volta a cada foco. */
const STALE = 1000 * 60 * 5;

/** Teto de linhas por resposta · 4.094 colaboradores nunca vêm de uma vez. */
export const COLLABORATORS_LIMIT = 200;

// Nas rotas com validator de query o `.json()` tipado é a união
// `{ data } | { error, code }` (o envelope de 400 entra no tipo), e nem `ok` nem
// `status` discriminam essa união. O `'data' in json` resolve sem cast: os tipos
// declarados acima continuam sendo conferidos contra a resposta real da API.
const NO_DATA = 'Resposta inesperada da API';

async function fetchUnits(): Promise<AccessUnit[]> {
  const res = await apiClient.api.totvs.units.$get();
  if (!res.ok) throw await readApiError(res, 'Falha ao carregar as unidades');
  const json = await res.json();
  return json.data;
}

async function fetchSectors(units: string): Promise<AccessSector[]> {
  const res = await apiClient.api.totvs.sectors.$get({ query: { units } });
  if (!res.ok) throw await readApiError(res, 'Falha ao carregar os setores');
  const json = await res.json();
  if (!('data' in json)) throw new Error(NO_DATA);
  return json.data;
}

async function fetchCollaborators(query: {
  units: string;
  sectors: string;
  search: string;
  limit: number;
}): Promise<AccessCollaborator[]> {
  const res = await apiClient.api.totvs.collaborators.$get({
    query: {
      units: query.units,
      sectors: query.sectors,
      search: query.search,
      limit: String(query.limit),
    },
  });
  if (!res.ok) throw await readApiError(res, 'Falha ao carregar os colaboradores');
  const json = await res.json();
  if (!('data' in json)) throw new Error(NO_DATA);
  return json.data;
}

/**
 * Chave estável para lista de filtros: ordem do clique não invalida o cache.
 *
 * A ordenação é o ponto central desta função — escolher "11, 1" e "1, 11" tem de
 * produzir a MESMA `queryKey`, senão o TanStack Query trata como duas consultas
 * e refaz a ida à API só porque o usuário clicou noutra ordem. Por isso o
 * comparador é explícito (`localeCompare`) em vez do `sort()` sem argumento: o
 * default converte para string e compara por unidade de código UTF-16, o que é
 * frágil de ler e de raciocinar. Com comparador declarado, o MESMO conjunto de
 * valores gera sempre a mesma chave (e a ordem enviada à API é irrelevante — os
 * dois lados só usam a lista como `IN (...)`).
 */
function listKey(values: string[]): string {
  return [...values].sort((a, b) => a.localeCompare(b)).join(',');
}

/** Espera o usuário parar de digitar antes de bater na API. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Hooks ────────────────────────────────────
/** Unidades (filiais) do TOTVS · lista fechada, carrega uma vez. */
export function useUnits() {
  return useQuery<AccessUnit[]>({
    queryKey: ['totvs', 'units'],
    queryFn: fetchUnits,
    staleTime: STALE,
  });
}

/**
 * Setores das unidades escolhidas, em DISTINCT por nome. Só consulta quando há
 * pelo menos uma unidade — sem filtro a resposta seriam os 204 setores, que não
 * significam nada para o formulário.
 */
export function useSectors(units: string[]) {
  const key = listKey(units);
  return useQuery<AccessSector[]>({
    queryKey: ['totvs', 'sectors', key],
    enabled: key !== '',
    queryFn: () => fetchSectors(key),
    staleTime: STALE,
    // mantém a lista anterior visível enquanto a nova carrega (sem piscar)
    placeholderData: (prev) => prev,
  });
}

export interface UseCollaboratorsArgs {
  units: string[];
  sectors: string[];
  /** Texto digitado no painel · debounce aplicado aqui dentro. */
  search: string;
  /** Só liga quando o toggle de confidencialidade por colaborador está ativo. */
  enabled?: boolean;
  limit?: number;
}

/**
 * Colaboradores dos setores/unidades escolhidos. SEMPRE com `search` (debounce
 * de 300 ms) e `limit` — a base tem 4.094 pessoas e carregar tudo derrubaria a
 * lista do painel.
 */
export function useCollaborators({
  units,
  sectors,
  search,
  enabled = true,
  limit = COLLABORATORS_LIMIT,
}: UseCollaboratorsArgs) {
  const term = useDebounced(search.trim(), 300);
  const unitsKey = listKey(units);
  const sectorsKey = listKey(sectors);
  return useQuery<AccessCollaborator[]>({
    queryKey: ['totvs', 'collaborators', unitsKey, sectorsKey, term, limit],
    enabled: enabled && sectorsKey !== '',
    queryFn: () =>
      fetchCollaborators({ units: unitsKey, sectors: sectorsKey, search: term, limit }),
    staleTime: STALE,
    placeholderData: (prev) => prev,
  });
}

/**
 * Busca livre de colaboradores em toda a base do RH, sem exigir unidade ou
 * setor. Serve para escolher o responsável de uma aplicação: o dono é uma
 * pessoa nomeada da empresa, não um nome digitado à mão. Só consulta a partir
 * de 2 caracteres, com o mesmo debounce dos demais.
 */
export function useCollaboratorSearch(search: string, limit = 20) {
  const term = useDebounced(search.trim(), 300);
  return useQuery<AccessCollaborator[]>({
    queryKey: ['totvs', 'collaborator-search', term, limit],
    enabled: term.length >= 2,
    queryFn: () => fetchCollaborators({ units: '', sectors: '', search: term, limit }),
    staleTime: STALE,
    placeholderData: (prev) => prev,
  });
}
